import React, { useEffect, useMemo, useRef, useState } from 'react';
import { safeArray } from '../../../components/ErrorBoundary';
import { useProjects } from '../../../context/ProjectsContext';
import ProjectFilterSelect from '../../../components/ProjectFilterSelect';
import TableEmptyRow from '../../../components/TableEmptyRow';
import StatusBadge from '../../../components/StatusBadge';
import { bgReportsAPI } from '../../../services/bgFinanceService';
import { DEPOSIT_TYPE_OPTIONS, formatCurrency, ISSUE_MODE_LABELS, LIMIT_TYPE_LABELS, RENEWAL_STATUS_LABELS } from './bgShared';
import { notifyError } from '../../../utils/notify';
import BgExportButtons from './BgExportButtons';
import { buildBgExportSubtitle, downloadBgTableCsv, exportBgTablePdf } from './bgExportUtils';
import { formatDateIN } from '../../../utils/dateFormat';

const REPORTS = [
  {
    key: 'bank-wise-eligibility',
    label: 'Bank-wise Eligibility',
    api: (params) => bgReportsAPI.getBankWiseEligibility(params),
    needsProject: false,
    columns: [
      { key: 'bank_name', label: 'Bank' },
      { key: 'limit_name', label: 'Limit Name' },
      { key: 'limit_type', label: 'Limit Type', render: (v) => LIMIT_TYPE_LABELS[v] || v },
      { key: 'sanctioned_amount', label: 'Sanctioned', amount: true },
      { key: 'eligible_amount', label: 'Eligible', amount: true },
      { key: 'utilized', label: 'Utilized', amount: true },
      { key: 'available', label: 'Available', amount: true },
      { key: 'utilization_percent', label: 'Utilization %', percent: true },
    ],
  },
  {
    key: 'amount-vs-bank',
    label: 'Amount vs Bank',
    api: (params) => bgReportsAPI.getAmountVsBank(params),
    needsProject: false,
    columns: [
      { key: 'bank_name', label: 'Bank' },
      { key: 'total_bg_bond_amount', label: 'Total BG/Bond', amount: true },
      { key: 'limit_backed_amount', label: 'Limit-backed', amount: true },
      { key: 'full_cash_backed_amount', label: '100% Cash-backed', amount: true },
      { key: 'full_cash_backed_counted_against_limit', label: 'Counted', amount: true },
      { key: 'full_cash_backed_not_counted_against_limit', label: 'Not Counted', amount: true },
      { key: 'cash_margin_amount', label: 'Cash Margin', amount: true },
      { key: 'active_amount', label: 'Active Amount', amount: true },
      { key: 'released_amount', label: 'Released', amount: true },
    ],
  },
  {
    key: 'cash-margin-register',
    label: 'Cash Margin Register',
    api: (params) => bgReportsAPI.getCashMarginRegister(params),
    needsProject: true,
    columns: [
      { key: 'bank', label: 'Bank' },
      { key: 'project', label: 'Project' },
      { key: 'bg_no', label: 'BG/Bond No.' },
      { key: 'deposit_type', label: 'Deposit Type', render: (v) => DEPOSIT_TYPE_OPTIONS.find((o) => o.value === v)?.label || v },
      { key: 'deposit_amount', label: 'Deposit', amount: true },
      { key: 'released_amount', label: 'Released', amount: true },
      { key: 'balance_cash_blocked', label: 'Balance Blocked', amount: true },
      { key: 'deposit_date', label: 'Deposit Date', date: true },
      { key: 'reference_no', label: 'Reference' },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'full-cash-backed',
    label: '100% Cash-backed BG/Bond',
    api: (params) => bgReportsAPI.getFullCashBacked(params),
    needsProject: false,
    columns: [
      { key: 'bg_no', label: 'BG/Bond No.' },
      { key: 'bank_name_text', label: 'Bank', render: (v, row) => v || row.bank?.bank_name || '—' },
      { key: 'project', label: 'Project', render: (v, row) => row.project?.name || '—' },
      { key: 'amount', label: 'Amount', amount: true },
      { key: 'counts_against_bank_limit', label: 'Counts Against Limit', render: (v) => (v ? 'Yes' : 'No') },
      { key: 'expiry_date', label: 'Expiry', date: true },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'project-wise-exposure',
    label: 'Project-wise BG Exposure',
    api: (params) => bgReportsAPI.getProjectWiseExposure(params),
    needsProject: true,
    columns: [
      { key: 'project', label: 'Project' },
      { key: 'bank', label: 'Bank' },
      { key: 'bg_no', label: 'BG/Bond No.' },
      { key: 'bg_type', label: 'BG Type' },
      { key: 'amount', label: 'Amount', amount: true },
      { key: 'issue_mode', label: 'Issue Mode', render: (v) => ISSUE_MODE_LABELS[v] || v },
      { key: 'expiry_date', label: 'Expiry', date: true },
      { key: 'status', label: 'Status' },
    ],
  },
  {
    key: 'expiry-renewal',
    label: 'Expiry & Renewal',
    api: (params) => bgReportsAPI.getExpiryRenewal(params),
    needsProject: false,
    columns: [
      { key: 'bg_no', label: 'BG/Bond No.' },
      { key: 'project', label: 'Project' },
      { key: 'bank', label: 'Bank' },
      { key: 'beneficiary', label: 'Beneficiary' },
      { key: 'amount', label: 'Amount', amount: true },
      { key: 'issue_mode', label: 'Issue Mode', render: (v) => ISSUE_MODE_LABELS[v] || v },
      { key: 'expiry_date', label: 'Expiry Date', date: true },
      { key: 'claim_expiry_date', label: 'Claim Expiry', date: true },
      { key: 'days_to_expiry', label: 'Days to Expiry' },
      { key: 'renewal_status_label', label: 'Renewal Status', render: (v, row) => v || RENEWAL_STATUS_LABELS[row.renewal_status] || row.renewal_status || '—' },
      { key: 'renewal_count', label: 'Renewal Count' },
      { key: 'last_renewed_at', label: 'Last Renewed', date: true },
      { key: 'action_required', label: 'Action Required' },
      { key: 'status', label: 'Status' },
    ],
    filterOptions: [
      { value: 'all', label: 'All' },
      { value: 'expired', label: 'Expired' },
      { value: 'expiring7', label: 'Expiring in 7 days' },
      { value: 'expiring15', label: 'Expiring in 15 days' },
      { value: 'expiring30', label: 'Expiring in 30 days' },
      { value: 'renewal_requested', label: 'Renewal Requested' },
      { value: 'renewed', label: 'Renewed' },
      { value: 'closed_released', label: 'Closed / Released' },
    ],
  },
  {
    key: 'renewal-history',
    label: 'Renewal History Report',
    api: (params) => bgReportsAPI.getRenewalHistory(params),
    needsProject: true,
    columns: [
      { key: 'project', label: 'Project' },
      { key: 'bank', label: 'Bank' },
      { key: 'bg_no', label: 'BG/Bond No.' },
      { key: 'renewal_no', label: 'Renewal No.' },
      { key: 'renewal_date', label: 'Renewal Date', date: true },
      { key: 'previous_expiry_date', label: 'Previous Expiry', date: true },
      { key: 'new_expiry_date', label: 'New Expiry', date: true },
      { key: 'previous_amount', label: 'Previous Amount', amount: true },
      { key: 'new_amount', label: 'New Amount', amount: true },
      { key: 'renewed_by', label: 'Renewed By' },
      { key: 'renewal_charges', label: 'Renewal Charges', amount: true },
      { key: 'remarks', label: 'Remarks' },
    ],
  },
  {
    key: 'type-wise-summary',
    label: 'Type-wise Summary',
    api: (params) => bgReportsAPI.getTypeWiseSummary(params),
    needsProject: false,
    columns: [
      { key: 'bg_type', label: 'BG Type' },
      { key: 'count', label: 'Count' },
      { key: 'amount', label: 'Total Amount', amount: true },
    ],
  },
];

const renderCell = (col, row) => {
  const raw = row[col.key];
  if (col.render) return col.render(raw, row);
  if (col.amount) return formatCurrency(raw);
  if (col.percent) return `${Number(raw || 0).toFixed(1)}%`;
  if (col.date) return formatDateIN(raw);
  if (col.key === 'status' && raw) return <StatusBadge tone={raw === 'Active' ? 'active' : 'draft'}>{raw}</StatusBadge>;
  return raw ?? '—';
};

const BgBondReports = () => {
  const { projects: projectOptions } = useProjects();
  const projects = safeArray(projectOptions);
  const [reportKey, setReportKey] = useState(REPORTS[0].key);
  const [projectFilter, setProjectFilter] = useState('all');
  const [expiryFilter, setExpiryFilter] = useState('all');
  const [rows, setRows] = useState([]);
  const tableRef = useRef(null);
  const [loading, setLoading] = useState(true);

  const currentReport = useMemo(
    () => REPORTS.find((r) => r.key === reportKey) || REPORTS[0],
    [reportKey],
  );

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const params = {};
        if (currentReport.needsProject && projectFilter !== 'all') {
          params.projectId = projectFilter;
        }
        if (currentReport.key === 'expiry-renewal' && expiryFilter !== 'all') {
          params.filter = expiryFilter;
        }
        const resp = await currentReport.api(params);
        setRows(safeArray(resp?.data?.data));
      } catch (err) {
        console.error('Load report failed:', err);
        notifyError(err?.response?.data?.message || 'Unable to load report.');
        setRows([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [reportKey, projectFilter, currentReport]);


  const reportSubtitle = buildBgExportSubtitle([
    currentReport.label,
    currentReport.needsProject && projectFilter !== 'all' ? `Project: ${projects.find((p) => p.id === parseInt(projectFilter, 10))?.name || projectFilter}` : null,
    currentReport.key === 'expiry-renewal' && expiryFilter !== 'all' ? `Filter: ${expiryFilter}` : null,
    `${rows.length} record(s)`,
  ]);

  const handleExportPdf = () => exportBgTablePdf({ title: currentReport.label, subtitle: reportSubtitle, element: tableRef.current });
  const handleExportCsv = () => downloadBgTableCsv({ element: tableRef.current, filename: `${currentReport.key}.csv` });

  return (
    <div>
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h2>BG / Bond Reports</h2>
        <BgExportButtons onDownloadPdf={handleExportPdf} onDownloadCsv={handleExportCsv} />
      </div>

      <div className="filter-bar">
        <select value={reportKey} onChange={(e) => setReportKey(e.target.value)}>
          {REPORTS.map((r) => (
            <option key={r.key} value={r.key}>{r.label}</option>
          ))}
        </select>
        {currentReport.filterOptions && (
        <select value={expiryFilter} onChange={(e) => setExpiryFilter(e.target.value)}>
          {currentReport.filterOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
        </select>
      )}
      {currentReport.needsProject && (
          <ProjectFilterSelect value={projectFilter} onChange={setProjectFilter} projects={projects} />
        )}
      </div>

      {loading ? (
        <p style={{ marginTop: 8, fontSize: 12, color: '#6b7280' }}>Loading report...</p>
      ) : (
        <div className="table-container" ref={tableRef}>
          <table className="data-table">
            <thead>
              <tr>
                {currentReport.columns.map((col) => (
                  <th key={col.key} className={col.amount ? 'amount-cell' : undefined}>{col.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <TableEmptyRow colSpan={currentReport.columns.length} message="No data for this report." />
              ) : (
                rows.map((row, idx) => (
                  <tr key={row.id || row.limit_id || row.bg_no || idx}>
                    {currentReport.columns.map((col) => (
                      <td key={col.key} className={col.amount ? 'amount-cell' : undefined}>
                        {renderCell(col, row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default BgBondReports;
